import { sanitizeRedirectPath } from '@opendoor/auth-fe/forms';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { ClientError } from 'graphql-request';
import { captureError } from 'helpers/captureError';

import { redactGraphqlRequestErrorForTelemetry } from '../api/graphqlRequestError';
import {
  CreatePasswordlessInstallResumeTokenDocument,
  SendPasswordlessSignInLinkDocument,
} from './__generated__/passwordless.zeus';
import type {
  CreatePasswordlessInstallResumeTokenMutationVariables,
  SendPasswordlessSignInLinkMutationVariables,
} from './__generated__/passwordless.zeus';

const INSTALL_RESUME_OPERATION = 'CreatePasswordlessInstallResumeToken';
const SEND_SIGN_IN_LINK_OPERATION = 'SendPasswordlessSignInLink';
type PasswordlessOperation = typeof INSTALL_RESUME_OPERATION | typeof SEND_SIGN_IN_LINK_OPERATION;

type PasswordlessTokenRequest = CreatePasswordlessInstallResumeTokenMutationVariables['input'] & {
  signal?: AbortSignal;
};

type SendPasswordlessSignInLinkRequest = SendPasswordlessSignInLinkMutationVariables & {
  signal?: AbortSignal;
};

type PasswordlessSignInLinkResult = { success: true } | { errors?: Array<string>; success: false };

type PasswordlessRedirectUrl = NonNullable<
  SendPasswordlessSignInLinkMutationVariables['input']['redirectUrl']
>;

export const resolvePasswordlessRedirectUrl = (
  redirectTo: string | null | undefined,
): PasswordlessRedirectUrl | undefined => {
  if (!redirectTo) {
    return undefined;
  }

  const safeRedirect = sanitizeRedirectPath(redirectTo);
  // Preserve the token page's root fallback for a supplied but unsafe destination. Omitting the
  // field lets Web substitute the customer's latest seller dashboard instead.
  return new URL(safeRedirect ?? '/', window.location.origin).href;
};

const reportRequestFailure = ({
  error,
  operation,
  signal,
}: {
  error: unknown;
  operation: PasswordlessOperation;
  signal?: AbortSignal;
}): void => {
  if (
    signal?.aborted ||
    (error instanceof Error && error.name === 'AbortError') ||
    (error instanceof ClientError && error.response.errors?.length)
  ) {
    return;
  }
  captureError(redactGraphqlRequestErrorForTelemetry(error), operation);
};

export const createPasswordlessInstallResumeToken = async ({
  passwordlessToken,
  signal,
}: PasswordlessTokenRequest): Promise<string | undefined> => {
  try {
    const response = await requestZeusFromBrowser(
      CreatePasswordlessInstallResumeTokenDocument,
      { input: { passwordlessToken } },
      signal,
    );
    const result = response.passwordlessInstallResumeTokenCreate;
    switch (result['__typename']) {
      case 'InstallResumeTokenCreation':
        return result.token ?? undefined;
      case 'InvalidInputError':
      case 'NotFoundError':
      case 'UnauthorizedError':
        return undefined;
      default:
        result satisfies never;
        return undefined;
    }
  } catch (error) {
    reportRequestFailure({ error, operation: INSTALL_RESUME_OPERATION, signal });
    return undefined;
  }
};

export const sendPasswordlessSignInLink = async ({
  input,
  signal,
}: SendPasswordlessSignInLinkRequest): Promise<PasswordlessSignInLinkResult> => {
  try {
    const response = await requestZeusFromBrowser(
      SendPasswordlessSignInLinkDocument,
      { input },
      signal,
    );
    const result = response.sendPasswordlessSignInLink;
    switch (result['__typename']) {
      case 'CommandResult':
        return result.success ? { success: true } : { success: false };
      case 'InvalidInputError':
      case 'NotFoundError':
      case 'UnauthorizedError':
        return { errors: [result.message], success: false };
      default:
        result satisfies never;
        return { success: false };
    }
  } catch (error) {
    reportRequestFailure({ error, operation: SEND_SIGN_IN_LINK_OPERATION, signal });
    return { success: false };
  }
};
